import { Check, ChevronDown, Languages } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  supportedLanguages,
  useI18n,
  type LanguagePreference,
} from "../i18n";

export function LanguageSelector({
  variant = "settings",
}: {
  variant?: "rail" | "settings";
}) {
  const { locale, preference, setPreference, t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeLanguage = useMemo(
    () => supportedLanguages.find((language) => language.locale === locale),
    [locale],
  );

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const choose = (value: LanguagePreference) => {
    setPreference(value);
    setOpen(false);
  };
  const currentLabel =
    preference === "system"
      ? t("language.systemShort")
      : activeLanguage?.nativeName ?? locale;

  return (
    <div
      className={`language-selector language-selector-${variant} ${open ? "open" : ""}`}
      ref={rootRef}
    >
      <button
        className={variant === "rail" ? "rail-button" : "language-selector-trigger"}
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={variant === "rail" ? t("nav.language") : undefined}
        aria-label={t("nav.language")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Languages size={variant === "rail" ? 17 : 15} strokeWidth={1.6} />
        {variant === "rail" ? (
          <small>{activeLanguage?.shortName}</small>
        ) : (
          <>
            <span>{currentLabel}</span>
            <ChevronDown size={14} />
          </>
        )}
      </button>
      {open ? (
        <div className="language-selector-menu" role="menu" aria-label={t("language.title")}>
          <header>
            <strong>{t("language.title")}</strong>
            <small>{t("language.description")}</small>
          </header>
          <LanguageOption
            active={preference === "system"}
            label={t("language.system")}
            meta={activeLanguage?.nativeName ?? locale}
            onClick={() => choose("system")}
          />
          <div className="language-selector-list">
            {supportedLanguages.map((language) => (
              <LanguageOption
                active={preference === language.locale}
                key={language.locale}
                label={language.nativeName}
                meta={language.locale}
                onClick={() => choose(language.locale)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LanguageOption({
  active,
  label,
  meta,
  onClick,
}: {
  active: boolean;
  label: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <button
      className={active ? "active" : ""}
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
    >
      <span><strong>{label}</strong><small>{meta}</small></span>
      {active ? <Check size={14} /> : null}
    </button>
  );
}
